import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import mean from 'lodash/mean';
import { EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import { getExperimentCohort } from '@atlassian/generative-ai-modal/utils/experiments';
import { addAnalytics, withAnalytics } from '../../analytics/utils';
import { DiffMatchPatch } from '../../utils/diff-match-patch/diff-match-patch';
import { median } from '../../utils/median';
import { aiSpellingGrammarPluginKey } from './ai-spelling-grammar-plugin-key';
import { disableNeedSpellingAndGrammar, insertSuggestion, removeSuggestion, startSpellingAndGrammar, toggleSpellingAndGrammar } from './commands';
import { END_PUNC_CHARS, MIDDLE_PUNC_CHARS, START_PUNC_CHARS } from './constants';
import { isFullStopOmittedInContainerType } from './utils';
var getCommonAnalyticsAttributes = function getCommonAnalyticsAttributes() {
  var variation = getExperimentCohort('editor_ai_-_proactive_ai_model_variations');
  if (variation !== 'control' && variation !== 'not-enrolled') {
    return {
      variation: variation
    };
  }
  return {
    variation: 'default'
  };
};
var getSuggestionMetadata = function getSuggestionMetadata(_ref) {
  var blockOrSentenceFromDiffObject = _ref.blockOrSentenceFromDiffObject,
    schema = _ref.schema;
  if (!blockOrSentenceFromDiffObject) {
    return;
  }
  var metadata = _objectSpread(_objectSpread({}, blockOrSentenceFromDiffObject === null || blockOrSentenceFromDiffObject === void 0 ? void 0 : blockOrSentenceFromDiffObject.metadata), {}, {
    inlineNodeCount: blockOrSentenceFromDiffObject === null || blockOrSentenceFromDiffObject === void 0 ? void 0 : blockOrSentenceFromDiffObject.ignoredRanges.filter(function (_ref2) {
      var type = _ref2.type;
      return type === 'inlineNode';
    }).length,
    contextualFormatting: isFullStopOmittedInContainerType(schema, blockOrSentenceFromDiffObject.containerType)
  });
  return metadata;
};
export var getPunctuationAttributes = function getPunctuationAttributes(diffObject) {
  var text = diffObject.text,
    originalText = diffObject.originalText;
  if (!originalText || !text ||
  // if it's the same length or 1 character difference between the texts
  !(originalText.length === text.length || Math.abs(originalText.length - text.length) === 1)) {
    return {
      isFullStopAdded: false,
      isPunctuationChanged: false
    };
  }
  var diffs = DiffMatchPatch.diff_main(originalText, text, false);
  switch (diffs.length) {
    case 2:
      {
        var _diffs = _slicedToArray(diffs, 2),
          diff1 = _diffs[0],
          diff2 = _diffs[1];
        // If there's only 1 addition in the diff at the end
        if (diff1[0] === 0 && diff2[0] === 1) {
          return {
            isFullStopAdded: diff2[1] === '.',
            isPunctuationChanged: END_PUNC_CHARS.includes(diff2[1])
          };
        }
        // If there's only 1 addition in the diff at the beginning
        if (diff1[0] === 1 && diff2[0] === 0) {
          return {
            isFullStopAdded: false,
            isPunctuationChanged: START_PUNC_CHARS.includes(diff1[1])
          };
        }
        break;
      }
    case 3:
      {
        var _diffs2 = _slicedToArray(diffs, 3),
          _diff = _diffs2[0],
          _diff2 = _diffs2[1],
          diff3 = _diffs2[2];
        // If punctuation was added or removed in the middle
        if (_diff[0] === 0 && _diff2[0] !== 0 && diff3[0] === 0) {
          return {
            isFullStopAdded: false,
            isPunctuationChanged: MIDDLE_PUNC_CHARS.includes(_diff2[1])
          };
        }
        break;
      }
    case 4:
      {
        var _diffs3 = _slicedToArray(diffs, 4),
          _diff3 = _diffs3[0],
          _diff4 = _diffs3[1],
          _diff5 = _diffs3[2],
          diff4 = _diffs3[3];
        // If there's 1 replacement (deletion + addition) in the middle
        if (_diff3[0] === 0 && _diff4[0] === -1 && _diff5[0] === 1 && diff4[0] === 0) {
          return {
            isFullStopAdded: false,
            isPunctuationChanged: MIDDLE_PUNC_CHARS.includes(_diff4[1]) && MIDDLE_PUNC_CHARS.includes(_diff5[1])
          };
        }
        break;
      }
    default:
      {
        break;
      }
  }
  return {
    isFullStopAdded: false,
    isPunctuationChanged: false
  };
};
export var insertSuggestionWithAnalytics = function insertSuggestionWithAnalytics(_ref3) {
  var aiInteractionID = _ref3.aiInteractionID,
    selectedDiff = _ref3.selectedDiff,
    schema = _ref3.schema,
    blockOrSentenceFromDiffObject = _ref3.blockOrSentenceFromDiffObject;
  return withAnalytics({
    payload: {
      action: 'inserted',
      actionSubject: 'editorPluginAI',
      actionSubjectId: 'spellingSuggestion',
      attributes: _objectSpread(_objectSpread(_objectSpread({
        aiInteractionID: aiInteractionID
      }, getPunctuationAttributes(selectedDiff)), getCommonAnalyticsAttributes()), getSuggestionMetadata({
        blockOrSentenceFromDiffObject: blockOrSentenceFromDiffObject,
        schema: schema
      })),
      eventType: EVENT_TYPE.TRACK
    }
  })(insertSuggestion(selectedDiff));
};
export var removeSuggestionWithAnalytics = function removeSuggestionWithAnalytics(_ref4) {
  var aiInteractionID = _ref4.aiInteractionID,
    schema = _ref4.schema,
    selectedDiff = _ref4.selectedDiff,
    blockOrSentenceFromDiffObject = _ref4.blockOrSentenceFromDiffObject;
  return withAnalytics({
    payload: {
      action: 'dismissed',
      actionSubject: 'editorPluginAI',
      actionSubjectId: 'spellingSuggestion',
      attributes: _objectSpread(_objectSpread(_objectSpread({
        aiInteractionID: aiInteractionID
      }, getPunctuationAttributes(selectedDiff)), getCommonAnalyticsAttributes()), getSuggestionMetadata({
        blockOrSentenceFromDiffObject: blockOrSentenceFromDiffObject,
        schema: schema
      })),
      eventType: EVENT_TYPE.TRACK
    }
  })(removeSuggestion(selectedDiff.originalText));
};
export var initiateProactiveAIWithAnalytics = function initiateProactiveAIWithAnalytics(aiInteractionID) {
  return withAnalytics({
    payload: {
      action: 'initiated',
      actionSubject: 'aiInteraction',
      actionSubjectId: 'editorPluginAI',
      attributes: _objectSpread(_objectSpread({}, getCommonAnalyticsAttributes()), {}, {
        singleInstrumentationID: aiInteractionID,
        aiInteractionID: aiInteractionID,
        aiFeatureName: 'Editor AI',
        aiExperienceName: 'Proactive Spelling and Grammar',
        proactiveAIGenerated: 1,
        userGeneratedAI: 0,
        isAIFeature: 1
      }),
      eventType: EVENT_TYPE.TRACK
    }
  });
};
export var handleSpellingAndGrammarWithAnalytics = function handleSpellingAndGrammarWithAnalytics(_ref5) {
  var proactiveToggleCount = _ref5.proactiveToggleCount,
    totalSuggestionsOnPage = _ref5.totalSuggestionsOnPage,
    dismissedWords = _ref5.dismissedWords,
    insertionCount = _ref5.insertionCount,
    initialToggleState = _ref5.initialToggleState,
    triggeredFrom = _ref5.triggeredFrom,
    operationType = _ref5.operationType;
  var analyticsPayload = {
    action: 'toggled',
    actionSubject: 'editorPluginAI',
    actionSubjectId: 'spellingSuggestion',
    attributes: _objectSpread(_objectSpread({}, getCommonAnalyticsAttributes()), {}, {
      toggledCount: proactiveToggleCount,
      initialToggleState: initialToggleState,
      totalSuggestions: totalSuggestionsOnPage,
      totalAcceptedSuggestions: insertionCount,
      totalDismissedSuggestions: dismissedWords,
      triggeredFrom: triggeredFrom
    }),
    eventType: EVENT_TYPE.TRACK
  };
  var operation = operationType === 'start' ? startSpellingAndGrammar : toggleSpellingAndGrammar;
  var operationWithAnalytics = withAnalytics({
    payload: analyticsPayload
  })(operation(proactiveToggleCount));
  return operationWithAnalytics;
};
var getFireAPIReceivedAnalytics = function getFireAPIReceivedAnalytics() {
  var apiReceivedCount = 0;
  var apiReceivedSamplingRate = 10;
  var durationArray = [];
  return function (_ref6) {
    var view = _ref6.view,
      duration = _ref6.duration,
      totalSuggestions = _ref6.totalSuggestions,
      totalAcceptedSuggestions = _ref6.totalAcceptedSuggestions,
      totalDismissedSuggestions = _ref6.totalDismissedSuggestions;
    apiReceivedCount++;
    durationArray.push(duration);
    if (apiReceivedCount >= apiReceivedSamplingRate) {
      /**
       * min / max will not be undefined as this function samples
       * durations after X amount of times, and this is only calculated
       * at the end of the sampling
       */
      var minDuration = Math.min.apply(Math, _toConsumableArray(durationArray));
      var maxDuration = Math.max.apply(Math, _toConsumableArray(durationArray));
      var meanDuration = mean(durationArray);
      var medianDuration = median(durationArray);
      var tr = addAnalytics({
        editorState: view.state,
        tr: view.state.tr,
        payload: {
          action: 'apiReceived',
          actionSubject: 'editorPluginAI',
          actionSubjectId: 'spellingSuggestion',
          attributes: _objectSpread(_objectSpread({}, getCommonAnalyticsAttributes()), {}, {
            meanDuration: meanDuration,
            medianDuration: medianDuration,
            minDuration: minDuration,
            maxDuration: maxDuration,
            totalSuggestions: totalSuggestions,
            totalAcceptedSuggestions: totalAcceptedSuggestions,
            totalDismissedSuggestions: totalDismissedSuggestions
          }),
          eventType: EVENT_TYPE.OPERATIONAL
        }
      });
      view.dispatch(tr);

      // reset sampling
      apiReceivedCount = 0;
      durationArray = [];
    }
  };
};
export var fireAPIReceivedAnalytics = getFireAPIReceivedAnalytics();
export var disableCheckForFailedChunksWithAnalytics = function disableCheckForFailedChunksWithAnalytics(_ref7) {
  var view = _ref7.view,
    failedChunkIds = _ref7.failedChunkIds,
    errors = _ref7.errors,
    reason = _ref7.reason,
    statusCode = _ref7.statusCode;
  return disableNeedSpellingAndGrammar(failedChunkIds, function (tr) {
    var uniqueInteractionID = tr.getMeta(aiSpellingGrammarPluginKey);
    var trWithAnalytics = tr;
    errors.forEach(function (error) {
      trWithAnalytics = addAnalytics({
        editorState: view.state,
        tr: trWithAnalytics,
        payload: {
          action: 'apiError',
          actionSubject: 'editorPluginAI',
          actionSubjectId: 'spellingSuggestion',
          attributes: _objectSpread(_objectSpread({}, getCommonAnalyticsAttributes()), {}, {
            error: error,
            reason: reason,
            statusCode: statusCode
          }),
          eventType: EVENT_TYPE.OPERATIONAL
        }
      });
    });
    trWithAnalytics = addAnalytics({
      editorState: view.state,
      tr: trWithAnalytics,
      payload: {
        action: 'error',
        actionSubject: 'aiResult',
        actionSubjectId: 'editorPluginAI',
        eventType: EVENT_TYPE.TRACK,
        attributes: {
          singleInstrumentationID: uniqueInteractionID || '',
          aiInteractionID: uniqueInteractionID || '',
          aiFeatureName: 'Editor AI',
          aiExperienceName: 'Proactive Spelling and Grammar',
          proactiveAIGenerated: 1,
          userGeneratedAI: 0,
          isAIFeature: 1,
          aiErrorMessage: reason,
          aiErrorCode: statusCode
        }
      }
    });
    return trWithAnalytics;
  });
};
export var disableCheckForPurgedChunksWithAnalytics = function disableCheckForPurgedChunksWithAnalytics(_ref8) {
  var purgedChunkIds = _ref8.purgedChunkIds,
    totalParts = _ref8.totalParts,
    totalPurgedParts = _ref8.totalPurgedParts;
  return withAnalytics({
    payload: {
      action: 'apiPurged',
      actionSubject: 'editorPluginAI',
      actionSubjectId: 'spellingSuggestion',
      attributes: _objectSpread(_objectSpread({}, getCommonAnalyticsAttributes()), {}, {
        totalParts: totalParts,
        totalPurgedParts: totalPurgedParts
      }),
      eventType: EVENT_TYPE.OPERATIONAL
    }
  })(disableNeedSpellingAndGrammar(purgedChunkIds));
};
export var viewSuggestionWithAnalytics = function viewSuggestionWithAnalytics(aiInteractionId, editorState) {
  return addAnalytics({
    editorState: editorState,
    tr: editorState.tr,
    payload: {
      action: 'viewed',
      actionSubject: 'aiResult',
      actionSubjectId: 'editorPluginAI',
      attributes: {
        aiFeatureName: 'Editor AI',
        aiInteractionID: aiInteractionId !== null && aiInteractionId !== void 0 ? aiInteractionId : '',
        singleInstrumentationID: aiInteractionId !== null && aiInteractionId !== void 0 ? aiInteractionId : '',
        proactiveAIGenerated: 1,
        userGeneratedAI: 0,
        isAIFeature: 1,
        aiExperienceName: 'Proactive Spelling and Grammar'
      },
      eventType: EVENT_TYPE.TRACK
    }
  });
};
export var ignoreSuggestionWithAnalytics = function ignoreSuggestionWithAnalytics(_ref9) {
  var aiInteractionID = _ref9.aiInteractionID,
    editorState = _ref9.editorState,
    selectedDiff = _ref9.selectedDiff,
    blockOrSentenceFromDiffObject = _ref9.blockOrSentenceFromDiffObject,
    transaction = _ref9.transaction;
  return addAnalytics({
    editorState: editorState,
    tr: transaction !== null && transaction !== void 0 ? transaction : editorState.tr,
    payload: {
      action: 'ignored',
      actionSubject: 'editorPluginAI',
      actionSubjectId: 'spellingSuggestion',
      attributes: _objectSpread(_objectSpread(_objectSpread({
        aiInteractionID: aiInteractionID
      }, getPunctuationAttributes(selectedDiff)), getCommonAnalyticsAttributes()), getSuggestionMetadata({
        schema: editorState.schema,
        blockOrSentenceFromDiffObject: blockOrSentenceFromDiffObject
      })),
      eventType: EVENT_TYPE.TRACK
    }
  });
};